import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from "@angular/core";

import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { SbCreative, SbCreativeType, State } from "@front/m19-api-client";
import { CollectionHeadlineMaxLength, sbAsinsToList } from "@front/m19-models";
import { BrandAssetsService, Constant, SbStrategyService } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  IAccordionComponent,
  IAlertComponent,
  IBadgeComponent,
  IButtonDirective,
  IButtonLinkDirective,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { CreativePreviewComponent } from "@m19-board/shared/creative-preview.component";
import { CreativeRejectedAdsComponent } from "@m19-board/sponsored-brand/creative-display/creative-rejected-ads.component";
import { AsinItemsPreviewComponent } from "@m19-board/strategies/sb-create-strategy/creative-preview-list/asin-items-preview/asin-items-preview.component";
import { VideoAsinPreviewComponent } from "@m19-board/strategies/sb-create-strategy/creative-preview-list/video-asin-preview/video-asin-preview.component";
import {
  SB_STRATEGIES_URL,
  TACOS_STRATEGIES_URL,
} from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-types";
import { finalize, switchMap } from "rxjs/operators";

export const SB_CREATIVE_TYPE_LABEL_KEYS: Record<SbCreativeType, string> = {
  [SbCreativeType.productCollection]: "creative-display.product_collection",
  [SbCreativeType.manualCollection]: "sb-form-ad-format.manual_collection",
  [SbCreativeType.video]: "sb-form-creative.video",
  [SbCreativeType.brandVideo]: "sb-form-ad-format.brand_video",
  [SbCreativeType.storeSpotlight]: "sb-form-ad-format.store_spotlight",
};

@Component({
  selector: "app-sb-creative-card",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    CreativePreviewComponent,
    CreativeRejectedAdsComponent,
    AsinItemsPreviewComponent,
    VideoAsinPreviewComponent,
    IAccordionComponent,
    IBadgeComponent,
    IButtonDirective,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuTriggerDirective,
    IIconComponent,
    IAlertComponent,
    IButtonLinkDirective,
  ],
  templateUrl: "./sb-creative-card.component.html",
})
export class SbCreativeCardComponent {
  private readonly sbStrategyService = inject(SbStrategyService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly brandAssetsService = inject(BrandAssetsService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly State = State;
  readonly SbCreativeType = SbCreativeType;
  readonly creativeTypeLabelKeys = SB_CREATIVE_TYPE_LABEL_KEYS;

  readonly creative = input.required<SbCreative>();
  readonly strategyId = input.required<number>();
  readonly creatives = input.required<SbCreative[]>();
  readonly readOnly = input(false);
  readonly tacosStrategyGroupId = input<number>();

  readonly storePages = toSignal(this.brandAssetsService.storePages$, {
    initialValue: [],
  });

  readonly canDeleteCreative = computed(() => this.creatives().length > 1);
  readonly isLastActiveCreative = computed(() => this.creatives().filter((c) => c.state === State.ON).length === 1);
  readonly isStoreSpotlight = computed(() => this.creative().creativeType === SbCreativeType.storeSpotlight);

  // TODO(Hugo|Clement|Joseph): remove this once all Product Collection creatives are migrated to Manual Collection
  readonly isProductCollection = computed(() => this.creative().creativeType === SbCreativeType.productCollection);
  readonly requiredHeadlineMigration = computed(
    () =>
      this.creative().creativeType === SbCreativeType.productCollection &&
      (this.creative().headline?.length ?? 0) > CollectionHeadlineMaxLength,
  );
  readonly requiredAsinsMigration = computed(
    () =>
      (this.creative().creativeType === SbCreativeType.productCollection &&
        this.creative().creativeAsins?.some(
          (asins) => sbAsinsToList(asins).length < Constant.minAsinPerManualCollectionAdLine,
        )) ??
      false,
  );
  readonly requireManualMigration = computed(() => {
    return this.requiredHeadlineMigration() || this.requiredAsinsMigration();
  });

  readonly storePage = computed(() => {
    return this.creative().storePageId
      ? this.storePages().find((page) => page.storePageId === this.creative().storePageId)
      : undefined;
  });

  private readonly opened = signal(false);
  readonly isOpened = this.opened.asReadonly();

  migrationLoading = signal(false);

  toggleOpened() {
    this.opened.update((open) => !open);
  }

  manageAssets() {
    this.navigateToEdit("assets");
  }

  manageAsins() {
    this.navigateToEdit("asins");
  }

  private navigateToEdit(step: "assets" | "asins") {
    const tacosStrategyGroupId = this.tacosStrategyGroupId();
    if (tacosStrategyGroupId != null) {
      void this.router.navigate([
        TACOS_STRATEGIES_URL,
        tacosStrategyGroupId,
        "sb",
        this.strategyId(),
        this.creative().creativeId,
        step,
      ]);
      return;
    }
    void this.router.navigate([SB_STRATEGIES_URL, this.strategyId(), this.creative().creativeId, step]);
  }

  toggleStatus() {
    const creative = this.creative();
    if (creative.state === State.ON) {
      if (this.isLastActiveCreative()) {
        this.toasterService.error(this.translocoService.translate("creative-display.cannot_pause_all"));
        return;
      }
      creative.state = State.OFF;
    } else {
      creative.state = State.ON;
    }

    this.sbStrategyService
      .updateSbCreativeStatus(creative)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toasterService.success(
            this.translocoService.translate("creative-display.status_updated_to_this_creative_state", [creative.state]),
          );
        },
        error: (err) => {
          this.toasterService.error(
            err,
            this.translocoService.translate("creative-display.sb_creative_status_update_error"),
          );
        },
      });
  }

  deleteCreative() {
    if (!this.canDeleteCreative()) return;

    const creative = this.creative();
    const modalRef = this.modalService.openModal(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("activity-service.creative_deletion"),
      data: {
        message: this.translocoService.translate("creative-display.delete_creative_confirm"),
        confirmLabel: this.translocoService.translate("common.delete"),
      },
    });

    modalRef
      .afterClosed()
      .pipe(
        switchMap(() => this.sbStrategyService.deleteSbCreative(creative)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(
            this.translocoService.translate("creative-display.sb_creative_successfully_deleted"),
          );
        },
        error: (err) => {
          this.toasterService.error(
            err,
            this.translocoService.translate("creative-display.sb_creative_deletion_error"),
          );
        },
      });
  }

  migrateToManualCollection() {
    if (this.requireManualMigration()) {
      this.manageAssets();
      return;
    }
    this.migrationLoading.set(true);
    this.sbStrategyService
      .migrateSbCreativeToManualCollection(this.creative())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.migrationLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(this.translocoService.translate("sb-creative-edit.product_collection_migrated"));
        },
        error: (err) => {
          this.toasterService.error(err, this.translocoService.translate("sb-creative-edit.update_error"));
        },
      });
  }
}
